/**
 * RouletteGame — Plan 08-03 Task 2 (GAME-02).
 *
 * 룰렛 휠 (8~16칸 prize) + 후원액 비례 회전 + seeded RNG 당첨.
 *
 * 결정론:
 *   - donationEventId 를 seed 로 createRouletteRng.
 *   - RNG.next() 로 cumulative probability 판정 → winner index.
 *   - 동일 eventId → 동일 winner. (T-08-03-04 repudiation 방어)
 *
 * 확률 공시:
 *   - meta.probabilityDisclosure = "꽝: 50.00% / 굿즈: 30.00% / ..." 문자열.
 *   - init(ctx) 시 renderDisclosure 로 stage 상단에 Text 배치.
 *   - disclosure 공란이면 즉시 throw (GAME-06).
 *
 * 렌더:
 *   - 휠은 Graphics 로 원 + 분할선 + prize 라벨.
 *   - reducedMotion=true → 회전 애니메이션 skip, 즉시 winner 표시.
 *   - reducedMotion=false → rotations × 2π + winnerAngle 까지 ease-out.
 *
 * 사행성 차단:
 *   - config 는 Zod RouletteConfigSchema 로 parse 됨 (registry) — 현금 환원 키 차단.
 *   - 본 클래스는 config.prizes 의 `value` 는 무시 (렌더 시 name 만 표시).
 */
import type { Container } from 'pixi.js';
import { Container as PixiContainer, Graphics, Text, TextStyle } from 'pixi.js';
import type {
  DonationEvent,
  Game,
  GameContext,
  GameMeta,
} from '../game-interface.js';
import type { RouletteConfig } from '@loop/contracts/rest';
import {
  renderDisclosure,
  buildPrizesDisclosure,
} from '../probability-disclosure.js';
import { createRouletteRng } from './roulette.rng.js';
import { pickCumulative } from '../seeded-rng.js';

export class RouletteGame implements Game {
  readonly meta: GameMeta;
  private wheel: Container | null = null;
  private ctx: GameContext | null = null;

  constructor(private readonly config: RouletteConfig) {
    const disclosure = buildPrizesDisclosure(config.prizes);
    if (disclosure.length === 0) {
      throw new Error('확률 공시 누락 — GAME-06');
    }
    this.meta = {
      kind: 'roulette',
      name: '룰렛',
      version: '1.0.0',
      prizes: config.prizes,
      probabilityDisclosure: disclosure,
    };
  }

  async init(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    const wheel = new PixiContainer();
    this.wheel = wheel;
    ctx.stage.addChild(wheel);

    // 원형 휠 — 단순 Graphics (WebGL 없이도 테스트에서 생성 가능).
    const g = new Graphics();
    const radius = 180;
    const cx = 400;
    const cy = 300;
    const n = this.config.prizes.length;
    const step = (Math.PI * 2) / n;
    const palette = [0xef4444, 0xf59e0b, 0x10b981, 0x3b82f6, 0x8b5cf6, 0xec4899];
    for (let i = 0; i < n; i += 1) {
      const start = i * step;
      const end = start + step;
      g.moveTo(cx, cy);
      g.arc(cx, cy, radius, start, end);
      g.lineTo(cx, cy);
      g.fill({ color: palette[i % palette.length], alpha: 0.85 });
    }
    g.circle(cx, cy, radius).stroke({ color: 0x111111, width: 3 });
    wheel.addChild(g);

    // prize labels
    const labelStyle = new TextStyle({
      fontFamily: 'system-ui, sans-serif',
      fontSize: 14,
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 2 },
    });
    for (let i = 0; i < n; i += 1) {
      const prize = this.config.prizes[i];
      if (!prize) continue;
      const mid = i * step + step / 2;
      const lx = cx + Math.cos(mid) * (radius * 0.65);
      const ly = cy + Math.sin(mid) * (radius * 0.65);
      const label = new Text({
        text: prize.name,
        style: labelStyle,
      });
      // anchor center — label.width/height 측정 시 jsdom 에서 font null 이슈 회피.
      label.anchor.set(0.5);
      label.x = lx;
      label.y = ly;
      wheel.addChild(label);
    }

    renderDisclosure(ctx.stage, this.meta.probabilityDisclosure, {
      reducedMotion: ctx.reducedMotion,
      position: 'bottom',
    });
  }

  async onDonation(event: DonationEvent): Promise<void> {
    if (!this.ctx) return;
    const rng = createRouletteRng(event.eventId);
    const r = rng.next();
    const probs = this.config.prizes.map((p) => p.probability);
    const winnerIndex = pickCumulative(probs, r);
    const winner = this.config.prizes[winnerIndex];

    // rotations = (amount / 1000) * rotationsPerKrw (최소 1).
    const rotations = Math.max(
      1,
      Math.floor(event.amount / 1000) * this.config.rotationsPerKrw,
    );

    if (!this.ctx.reducedMotion && this.wheel) {
      // MD-04: ease-out cubic 회전 애니메이션 (300ms * rotations, 최대 5000ms).
      // ticker 가 동작하지 않는 test/jsdom 환경에서도 안전 — registerGame 후
      // performance.now 가 없으면 즉시 targetAngle 로 설정.
      const wheel = this.wheel;
      const app = this.ctx.app;
      const startAngle = wheel.rotation;
      const targetAngle =
        startAngle +
        rotations * Math.PI * 2 +
        ((Math.PI * 2) / this.config.prizes.length) * winnerIndex;
      const duration = Math.min(300 * rotations, 5000);
      const now = typeof performance !== 'undefined' ? performance.now() : null;
      if (now == null || !app?.ticker) {
        wheel.rotation = targetAngle;
      } else {
        const startT = now;
        const tick = (): void => {
          const t = Math.min(1, (performance.now() - startT) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          wheel.rotation = startAngle + (targetAngle - startAngle) * eased;
          if (t >= 1) app.ticker.remove(tick);
        };
        app.ticker.add(tick);
      }
    } else if (this.wheel) {
      // reducedMotion → 즉시 winner 각도.
      this.wheel.rotation =
        ((Math.PI * 2) / this.config.prizes.length) * winnerIndex;
    }

    this.ctx.reportResult({
      kind: 'roulette',
      winner: { index: winnerIndex, prize: winner, rotations },
      donationEventId: event.eventId,
      at: new Date().toISOString(),
    });
  }

  destroy(): void {
    if (this.wheel) {
      this.wheel.removeFromParent();
      this.wheel.destroy({ children: true });
      this.wheel = null;
    }
    this.ctx = null;
  }
}
